import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface MaxMatchUserSummary {
  userId: number;
  email: string;
  companyName: string;
  clinicCount: number;
  maxMatchRunCount: number;
  maxMatchResultCount: number;
  settlementMonthCount: number;
}

export interface MaxMatchDetailItem {
  runId: number;
  createdAt: string;
  refYm: string;
  clinicId: number;
  clinicName: string;
  creationType: string;
  unchangedCount: number;
  changedCount: number;
  originEstimatedTotalAmount: number;
  newEstimatedTotalAmount: number;
}

export interface MaxMatchClinic {
  clinicId: number;
  clinicName: string;
}

export interface MaxMatchMiniItem {
  summaryId: number;
  email: string;
  companyName?: string;
  clinicName: string;
  prescriptionYearMonth: string;
  status: string;
  createdDate: string;
  /** 레거시 maxMaxMatchSummaryCreatedAt */
  maxMaxMatchSummaryCreatedAt?: string;
  /** 레거시 maxMatchMiniCreatedAt */
  maxMatchMiniCreatedAt?: string;
  confirmedImageCount?: number;
  totalImageCount?: number;
}

export type MaxMatchMiniImageReviewStatus =
  | 'UNCONFIRMED'
  | 'USER_CONFIRMED'
  | 'ADMIN_CONFIRMED'
  | 'UNVERIFIABLE';

export interface MaxMatchMiniImage {
  imageId: number;
  imageCode?: string;
  imageUrl?: string;
  status?: string;
  isConfirmed?: boolean;
  /** 미확정과 확인불가를 구분하려면 isConfirmed 대신 이 값을 본다. */
  reviewStatus?: string;
}

export interface MaxMatchMiniMr {
  mrId?: number;
  drugCode: string;
  drugName?: string;
  pharmaCompanyName?: string;
  price?: number;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
  hasDrugInfo?: boolean | null;
  __tempRowKey?: string;
}

/** POST …/mrs · …/mrs/batch — AdminMaxMatchMiniMrCreateRequest */
export interface MaxMatchMiniMrCreatePayload {
  drugCode: string;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

/** PUT …/mrs/{mrId} — AdminMaxMatchMiniMrUpdateRequest (drugName 필수) */
export interface MaxMatchMiniMrUpdatePayload {
  drugCode: string;
  drugName: string;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

/** @deprecated MaxMatchMiniMrCreatePayload 사용 */
export type MaxMatchMiniMrPayload = MaxMatchMiniMrCreatePayload;

export const mapMaxMatchMiniImage = (raw: unknown): MaxMatchMiniImage => {
  const row =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const isConfirmed = Boolean(row.isConfirmed ?? row.is_confirmed);
  const reviewStatus = String(row.reviewStatus ?? row.review_status ?? '');
  return {
    imageId: Number(row.imageId ?? row.image_id ?? 0),
    imageCode: String(row.imageCode ?? row.image_code ?? ''),
    imageUrl: String(row.imageUrl ?? row.image_url ?? ''),
    status: String(row.status ?? ''),
    isConfirmed,
    // 확인불가는 isConfirmed=false 이므로 미확정과 구분하려면 reviewStatus 가 필요하다
    reviewStatus:
      reviewStatus || (isConfirmed ? 'ADMIN_CONFIRMED' : 'UNCONFIRMED'),
  };
};

/**
 * 맥스매치 생성은 1시간 이상 걸릴 수 있는 동기 작업 — 기본 10초면 요청이 끊기므로 클라이언트 타임아웃을 끈다.
 * 서버 앞단(LB·프록시)의 유휴 타임아웃은 별도이며, 근본적으로는 서버 비동기 처리가 필요하다.
 */
const RUN_TIMEOUT_MS = 0;

export const maxMatchService = {
  runForMonth: async (prescriptionMonth: string): Promise<void> => {
    await apiClient.post(
      createApiPath.maxMatch.run(),
      { prescriptionMonth },
      { timeout: RUN_TIMEOUT_MS },
    );
  },

  search: async (
    params: Record<string, string | number | undefined>,
    pageable: Pageable,
  ): Promise<PageResponse<MaxMatchUserSummary>> => {
    const response = await apiClient.get(createApiPath.maxMatch.search(), {
      params: { ...params, page: pageable.page, size: pageable.size },
    });
    return response.data;
  },

  getExists: async (prescriptionYearMonth: string): Promise<boolean> => {
    const response = await apiClient.get(createApiPath.maxMatch.exists(), {
      params: { prescriptionYearMonth },
    });
    return Boolean(response.data);
  },

  publish: async (prescriptionYearMonth: string): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.publish(), {
      prescriptionYearMonth,
    });
  },

  getExcelDetails: async (
    params: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.excelDetails(),
      {
        params,
      },
    );
    return response.data;
  },

  getUserDetails: async (
    userId: number,
    params: Record<string, string | number | undefined>,
    pageable: Pageable,
  ): Promise<PageResponse<MaxMatchDetailItem>> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.userDetails(userId),
      { params: { ...params, page: pageable.page, size: pageable.size } },
    );
    const page = response.data as PageResponse<Record<string, unknown>>;
    // API 필드 별칭(snake_case / prescriptionYearMonth) 방어
    const content = (page.content ?? []).map((raw) => {
      const row = raw as Record<string, unknown>;
      return {
        runId: Number(row.runId ?? row.run_id ?? 0),
        createdAt: String(row.createdAt ?? row.created_at ?? ''),
        refYm: String(
          row.refYm ?? row.ref_ym ?? row.prescriptionYearMonth ?? '',
        ),
        clinicId: Number(row.clinicId ?? row.clinic_id ?? 0),
        clinicName: String(row.clinicName ?? row.clinic_name ?? ''),
        creationType: String(row.creationType ?? row.creation_type ?? ''),
        unchangedCount: Number(row.unchangedCount ?? row.unchanged_count ?? 0),
        changedCount: Number(row.changedCount ?? row.changed_count ?? 0),
        originEstimatedTotalAmount: Number(
          row.originEstimatedTotalAmount ??
            row.origin_estimated_total_amount ??
            0,
        ),
        newEstimatedTotalAmount: Number(
          row.newEstimatedTotalAmount ?? row.new_estimated_total_amount ?? 0,
        ),
      } satisfies MaxMatchDetailItem;
    });
    return { ...page, content } as PageResponse<MaxMatchDetailItem>;
  },

  getUserClinics: async (userId: number): Promise<MaxMatchClinic[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.userClinics(userId),
    );
    return response.data;
  },

  getOperatingHours: async () => {
    const response = await apiClient.get(
      createApiPath.maxMatch.operatingHours(),
    );
    return response.data;
  },

  getScreenWindows: async () => {
    const response = await apiClient.get(
      createApiPath.maxMatch.screenWindows(),
    );
    return response.data;
  },

  saveScreenWindow: async (
    screenType: string,
    payload: Record<string, unknown>,
  ) => {
    const response = await apiClient.put(
      createApiPath.maxMatch.screenWindow(screenType),
      payload,
    );
    return response.data;
  },

  miniRun: async (summaryId: number): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.miniRun(), { summaryId });
  },

  miniSearch: async (
    params: Record<string, string | number | boolean | undefined>,
    pageable: Pageable,
  ): Promise<PageResponse<MaxMatchMiniItem>> => {
    const response = await apiClient.get(createApiPath.maxMatch.miniSearch(), {
      params: { ...params, page: pageable.page, size: pageable.size },
    });
    const page = response.data as PageResponse<Record<string, unknown>>;
    const content = (page.content ?? []).map((raw) => {
      const row = raw as Record<string, unknown>;
      return {
        summaryId: Number(row.summaryId ?? row.summary_id ?? 0),
        email: String(row.email ?? ''),
        companyName: String(row.companyName ?? row.company_name ?? ''),
        clinicName: String(row.clinicName ?? row.clinic_name ?? ''),
        prescriptionYearMonth: String(
          row.prescriptionYearMonth ?? row.prescription_year_month ?? '',
        ),
        status: String(row.status ?? ''),
        createdDate: String(row.createdDate ?? row.created_date ?? ''),
        maxMaxMatchSummaryCreatedAt: String(
          row.maxMaxMatchSummaryCreatedAt ??
            row.max_max_match_summary_created_at ??
            row.createdDate ??
            '',
        ),
        maxMatchMiniCreatedAt: String(
          row.maxMatchMiniCreatedAt ?? row.max_match_mini_created_at ?? '',
        ),
        confirmedImageCount: Number(
          row.confirmedImageCount ?? row.confirmed_image_count ?? 0,
        ),
        totalImageCount: Number(
          row.totalImageCount ?? row.total_image_count ?? 0,
        ),
      } satisfies MaxMatchMiniItem;
    });
    return { ...page, content } as PageResponse<MaxMatchMiniItem>;
  },

  miniExcelDetails: async (
    summaryId: number,
  ): Promise<Record<string, unknown>[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.miniExcel(summaryId),
    );
    return response.data;
  },

  miniImages: async (summaryId: number): Promise<MaxMatchMiniImage[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.miniImages(summaryId),
    );
    const list = Array.isArray(response.data) ? response.data : [];
    // 레거시: imageId 오름차순 정렬 후 첫 이미지 선택
    return list.map(mapMaxMatchMiniImage).sort((a, b) => a.imageId - b.imageId);
  },

  miniMrs: async (imageId: number): Promise<MaxMatchMiniMr[]> => {
    const response = await apiClient.get(
      createApiPath.maxMatch.miniMrs(imageId),
    );
    return response.data;
  },

  addMiniMr: async (
    imageId: number,
    payload: MaxMatchMiniMrCreatePayload,
  ): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.miniMrs(imageId), payload);
  },

  addMiniMrsBatch: async (
    imageId: number,
    payload: MaxMatchMiniMrCreatePayload[],
  ): Promise<void> => {
    await apiClient.post(createApiPath.maxMatch.miniMrsBatch(imageId), payload);
  },

  deleteMiniMr: async (mrId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.miniMr(mrId));
  },

  deleteAllMiniMrs: async (imageId: number): Promise<void> => {
    await apiClient.delete(createApiPath.maxMatch.miniMrsDeleteAll(imageId));
  },

  updateMiniMr: async (
    mrId: number,
    payload: MaxMatchMiniMrUpdatePayload,
  ): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.miniMr(mrId), payload);
  },

  /** PUT only — 처방년월 수정. GET 엔드포인트는 없다. */
  updateMiniPrescriptionInfo: async (
    summaryId: number,
    payload: { prescriptionYearMonth: string },
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.maxMatch.miniPrescriptionInfo(summaryId),
      payload,
    );
  },

  confirmMiniImage: async (imageId: number): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.miniConfirmImage(imageId));
  },

  markMiniImageUnverifiable: async (imageId: number): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.miniUnverifiableImage(imageId));
  },

  miniConfirmBatch: async (imageIds: number[]): Promise<void> => {
    await apiClient.put(createApiPath.maxMatch.miniConfirmBatch(), {
      imageIds,
    });
  },

  /**
   * Lite 단건 GET 이 없어 목록에서 요약을 다시 읽는다.
   * 이미지 확정 후 내역 상태 동기화에 쓴다. isRequest 필터는 넣지 않는다.
   */
  findMiniSummary: async (
    summaryId: number,
    yearMonth: string,
    searchKeyword?: string,
  ): Promise<MaxMatchMiniItem | null> => {
    const searchOnce = async (keyword?: string) => {
      const page = await maxMatchService.miniSearch(
        {
          yearMonth,
          searchKeyword: keyword || undefined,
        },
        { page: 0, size: 50 },
      );
      return page.content.find((row) => row.summaryId === summaryId) ?? null;
    };
    const keyword = searchKeyword?.trim();
    return (await searchOnce(keyword)) ?? (keyword ? searchOnce() : null);
  },
};
